import { useEffect, useState, useRef } from "react";
import { http } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { UsersRound, ChevronDown, MessageSquare } from "lucide-react";
import SendMessageDialog from "@/components/SendMessageDialog";

/**
 * Sidebar badge that shows how many operators are currently online
 * (heartbeat within the last 90s) and — on click — expands a compact
 * dropdown listing each name + role. Clicking any operator (other
 * than yourself) opens a send-message dialog. Polls every 20s.
 *
 * Placement (top → bottom): above PrintHealthBadge, below the nav.
 */
export default function OperatorsOnlineBadge({ collapsed }) {
  const { user } = useAuth();
  const [data, setData] = useState({ count: 0, operators: [] });
  const [open, setOpen] = useState(false);
  const [msgTarget, setMsgTarget] = useState(null);
  const boxRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    const fetchNow = async () => {
      try {
        const r = await http.get("/users/online");
        if (!cancelled) setData(r.data || { count: 0, operators: [] });
      } catch {
        /* ignore — sidebar badge shouldn't spam toasts */
      }
    };
    fetchNow();
    const t = setInterval(fetchNow, 20000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  // Close popover on outside click
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  const count = data.count || 0;

  if (collapsed) {
    return (
      <div className="flex justify-center">
        <div
          className="relative w-9 h-9 rounded-md bg-emerald-100 border border-emerald-300 grid place-items-center text-emerald-800"
          title={`${count} χειριστές online`}
          data-testid="operators-online-badge-compact"
        >
          <UsersRound size={16} />
          <span className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 rounded-full bg-emerald-600 text-white text-[10px] font-bold flex items-center justify-center">
            {count}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="relative" ref={boxRef} data-testid="operators-online-badge">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid="operators-online-toggle"
        className="w-full flex items-center gap-2 rounded-md px-2 py-1.5 bg-emerald-50 border border-emerald-300 hover:bg-emerald-100 transition-colors text-left"
      >
        <UsersRound size={14} className="text-emerald-700" />
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-wider text-emerald-700 font-bold leading-tight">
            Χειριστές Online
          </div>
          <div className="text-xs text-emerald-900 font-bold leading-tight truncate">
            {count === 0 ? "Κανένας" : count === 1 ? "1 χειριστής" : `${count} χειριστές`}
          </div>
        </div>
        <ChevronDown
          size={12}
          className={`text-emerald-700 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>

      {open && (
        <div
          className="absolute left-0 right-0 bottom-full mb-1 z-30 rounded-md bg-white border border-slate-300 shadow-xl max-h-[280px] overflow-y-auto"
          data-testid="operators-online-list"
        >
          {count === 0 ? (
            <div className="px-3 py-4 text-center text-xs text-slate-500">
              Κανένας χειριστής online
            </div>
          ) : (
            <ul className="py-1">
              {/* Broadcast — «Προς όλους». Shown only when at least one
                  other operator (besides ourselves) is online, so a
                  lone cashier doesn't see a useless option. */}
              {data.operators.some((op) => op.id !== user?.id) && (
                <li
                  data-testid="operator-online-broadcast"
                  className="flex items-center gap-2 px-3 py-2 group hover:bg-amber-50 cursor-pointer border-b border-slate-100"
                  onClick={() => {
                    setMsgTarget({ id: "*", name: "όλους τους χειριστές", role: "broadcast" });
                    setOpen(false);
                  }}
                  title="Στείλε μήνυμα σε όλους τους online χειριστές"
                >
                  <span className="w-2 h-2 rounded-full bg-amber-500 shrink-0 shadow-[0_0_6px_rgba(245,158,11,0.8)]" />
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-bold text-slate-800 truncate">
                      Προς όλους
                    </div>
                    <div className="text-[10px] uppercase tracking-wider text-amber-700">
                      {data.operators.filter((op) => op.id !== user?.id).length} χειριστές
                    </div>
                  </div>
                  <MessageSquare
                    size={13}
                    className="text-amber-600 opacity-70 group-hover:opacity-100 transition-opacity shrink-0"
                  />
                </li>
              )}
              {data.operators.map((op) => {
                const isSelf = op.id === user?.id;
                return (
                  <li
                    key={op.id}
                    data-testid={`operator-online-${op.id}`}
                    className={`flex items-center gap-2 px-3 py-2 group ${
                      isSelf ? "" : "hover:bg-blue-50 cursor-pointer"
                    }`}
                    onClick={() => {
                      if (isSelf) return;
                      setMsgTarget({ id: op.id, name: op.name, role: op.role });
                      setOpen(false);
                    }}
                    title={isSelf ? "Είσαι εσύ" : "Στείλε μήνυμα"}
                  >
                    <span className="w-2 h-2 rounded-full bg-emerald-500 shrink-0 shadow-[0_0_6px_rgba(16,185,129,0.7)]" />
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-bold text-slate-800 truncate">
                        {op.name}
                        {isSelf && (
                          <span className="ml-1 text-[9px] text-slate-400 uppercase tracking-wider">
                            (εσύ)
                          </span>
                        )}
                      </div>
                      <div className="text-[10px] uppercase tracking-wider text-slate-500">
                        {op.role}
                      </div>
                    </div>
                    {!isSelf && (
                      <MessageSquare
                        size={13}
                        className="text-blue-500 opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
                      />
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
      <SendMessageDialog
        open={!!msgTarget}
        onOpenChange={(o) => !o && setMsgTarget(null)}
        target={msgTarget}
      />
    </div>
  );
}
